import React, { useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useScrollLock } from '../../../_utils/useScrollLock';
import { redirectToAuth } from './authRedirect';
import './AuthPromptModal.css';

// Icons
const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" fill="#ff4757" stroke="#ff4757" strokeWidth="2">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);

const AuthPromptModal = ({
  isOpen,
  onClose,
  pendingBookingData = null,
  context = 'booking', // 'booking' or 'favorite'
  // Guest booking (GUEST-BOOKING-PLAN.md §3). Only offered when the salon
  // allows it AND nothing is due online; when hidden for the payment reason
  // we say so, since the client may reasonably expect the option.
  guestBookingAllowed = false,
  guestBlockedByPayment = false,
  onContinueAsGuest = null,
}) => {
  const history = useHistory();
  const [isClosing, setIsClosing] = useState(false);
  useScrollLock(isOpen);

  // Handle close with animation
  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  }, [onClose]);

  // Stash any pending booking and leave for login / signup (shared helper so
  // this and the in-sheet auth step can't drift).
  const handleSignIn = useCallback(() => redirectToAuth(history, pendingBookingData, 'login'), [pendingBookingData, history]);
  const handleCreateAccount = useCallback(() => redirectToAuth(history, pendingBookingData, 'signup'), [pendingBookingData, history]);

  if (!isOpen) return null;

  // Content based on context
  const isFavoriteContext = context === 'favorite';
  const isGiftCardContext = context === 'giftcard';
  const isProductContext = context === 'product';
  const title = isFavoriteContext ? 'Sign in to save' : 'Sign in to continue';
  const message = isFavoriteContext
    ? 'Sign in or create an account to save your favorites and access them anytime.'
    : isGiftCardContext
      ? 'To buy a gift card, please sign in or create an account.'
      : isProductContext
        ? 'To complete your purchase, please sign in or create an account.'
        : 'To complete your booking, please sign in or create an account.';
  const Icon = isFavoriteContext ? HeartIcon : UserIcon;

  return (
    <div className={`auth-prompt-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`auth-prompt-modal ${isClosing ? 'closing' : ''}`} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="auth-prompt-header">
          <div className="auth-prompt-header-spacer" />
          <h2 className="auth-prompt-title">{title}</h2>
          <button className="auth-prompt-close" onClick={handleClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>

        {/* Body */}
        <div className="auth-prompt-body">
          <div className={`auth-prompt-icon ${isFavoriteContext ? 'auth-prompt-icon-heart' : ''}`}>
            <Icon />
          </div>
          
          <p className="auth-prompt-message">
            {message}
          </p>

          <div className="auth-prompt-buttons">
            <button
              className="auth-prompt-button auth-prompt-button-primary"
              onClick={handleSignIn}
            >
              Sign In
            </button>
            
            <button
              className="auth-prompt-button auth-prompt-button-secondary"
              onClick={handleCreateAccount}
            >
              Create Account
            </button>

            {context === 'booking' && guestBookingAllowed && !guestBlockedByPayment && onContinueAsGuest && (
              <>
                <div className="auth-prompt-divider"><span>or</span></div>
                <button
                  className="auth-prompt-button auth-prompt-button-ghost"
                  onClick={onContinueAsGuest}
                >
                  Continue as guest
                </button>
              </>
            )}
          </div>

          {context === 'booking' && guestBookingAllowed && guestBlockedByPayment && (
            <p className="auth-prompt-note">
              This booking needs a deposit or card details, so you'll need an account to pay securely.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPromptModal;
